import { ICreateComment } from '@sdk/contracts';
import { Button, Flex, Form, FormInstance, Input, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';

import {
  ButtonsWrapper,
  CancelWrapper,
  FileWrapper,
  FooterWrapper,
  InfoWrapper,
  Wrapper
} from './CreateCommentForm.styles';

import { Icon } from '@/components/Icon';
import { Upload } from '@/components/Upload';
import { useMutateEvents } from '@/store';

const ATTACHMENT_ICON_SIZE = 12;

type Props = {
  form: FormInstance;
  isActive: boolean;
  setIsActive: (isActive: boolean) => void;
  onFormSubmit: (values: ICreateComment) => void;
  onCancel?: () => void;
  alwaysExpanded?: boolean;
  canCancel?: boolean;
  placeholder?: string;
  isLoading?: boolean;
};

export const CreateCommentForm: React.FC<Props> = ({
  form,
  isActive,
  setIsActive,
  onFormSubmit,
  onCancel,
  alwaysExpanded = false,
  canCancel = true,
  placeholder,
  isLoading = false
}) => {
  const { t } = useTranslation('CommonEventTranslations');
  const content = Form.useWatch('content', form);
  const file = Form.useWatch('file', form);
  const { isWorking } = useMutateEvents();
  const handleCancel = () => {
    if (!alwaysExpanded && canCancel) {
      setIsActive(false);
    }
    form.resetFields();
    onCancel?.();
  };

  const onSubmit = (values: ICreateComment) => {
    onFormSubmit(values);
  };

  return (
    <Form layout="vertical" form={form} onFinish={onSubmit}>
      <Wrapper>
        {isActive && canCancel && (
          <CancelWrapper onClick={handleCancel}>
            <Icon i="Close" />
            <div>{t('CANCEL_MESSAGE')}</div>
          </CancelWrapper>
        )}
        <Form.Item noStyle name="content">
          <Input.TextArea
            autoFocus={isActive}
            allowClear
            disabled={isWorking}
            rows={isActive ? 4 : 1}
            placeholder={placeholder || t('WRITE_YOUR_MESSAGE')}
            onFocus={() => setIsActive(true)}
          />
        </Form.Item>
        {file && (
          <FileWrapper closable>
            <Flex align="center" gap="8px">
              <Icon size={ATTACHMENT_ICON_SIZE} i="Attachment" />
              <div>{file.name}</div>
            </Flex>
          </FileWrapper>
        )}
        {isActive && (
          <FooterWrapper>
            <Tooltip title={t('MESSAGE_TIPS_TOOLTIP')} placement="bottomLeft">
              <InfoWrapper>
                <Icon i="Information" />
                <div>{t('MESSAGE_TIPS')}</div>
              </InfoWrapper>
            </Tooltip>
            <ButtonsWrapper>
              <Form.Item noStyle name="file">
                <Upload
                  disabled={!!file || isWorking}
                  showUploadList={false}
                  iconOnly
                  mode="upload"
                  icon="Attachment"
                />
              </Form.Item>
              <Button
                type="primary"
                shape="circle"
                loading={isWorking || isLoading}
                icon={<Icon i="SendAlt" />}
                onClick={form.submit}
                disabled={!content}
              />
            </ButtonsWrapper>
          </FooterWrapper>
        )}
      </Wrapper>
    </Form>
  );
};
